// FILE: PullRequestTimelineTab.tsx
// Purpose: The `timeline` tab — the conversation and the commits, in the order they happened,
//          plus the box for adding to it.
// Layer: Component
// Exports: PullRequestTimelineTab
//
// One chronological stream rather than two lists: "he pushed a fix, then she approved it" is the
// thing a reviewer is trying to reconstruct, and two columns make the reader do the interleaving.
//
// Scope, deliberately: remarks are shown flat. Review threads are not nested, replies are not
// grouped under their parent, and reactions are not shown at all — none of the three is in the
// activity the server returns, and drawing a thread that is really a flat list would be a lie
// about who was answering whom.

import type {
  PullRequestActivity,
  PullRequestComment,
  PullRequestCommit,
  PullRequestRef,
} from "@taskin-code/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import ChatMarkdown from "~/components/ChatMarkdown";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { Textarea } from "~/components/ui/textarea";
import { toastManager } from "~/components/ui/toast";
import { GitCommitIcon, MessageIcon } from "~/lib/icons";
import { pullRequestsCommentMutationOptions } from "~/lib/pullRequestsReactQuery";
import { formatRelativeTime } from "~/lib/relativeTime";
import { cn } from "~/lib/utils";
import { PullRequestAuthorChip, readPullRequestErrorMessage } from "./pullRequestPresentation";
import { PullRequestFailure } from "./PullRequestFailure";

const REVIEW_STATE_CLASS: Record<string, string> = {
  approved: "text-emerald-600 dark:text-emerald-300/90",
  changes_requested: "text-destructive",
  commented: "text-muted-foreground",
  dismissed: "text-muted-foreground/70",
  pending: "text-amber-600 dark:text-amber-300/90",
};

const REVIEW_STATE_LABELS: Record<string, string> = {
  approved: "approved",
  changes_requested: "requested changes",
  commented: "reviewed",
  dismissed: "review dismissed",
  pending: "review pending",
};

type TimelineItem =
  | { kind: "comment"; at: string; comment: PullRequestComment }
  | { kind: "commit"; at: string; commit: PullRequestCommit };

function buildTimeline(activity: PullRequestActivity): ReadonlyArray<TimelineItem> {
  const items: TimelineItem[] = [
    ...activity.comments.map(
      (comment): TimelineItem => ({ kind: "comment", at: comment.createdAt, comment }),
    ),
    ...activity.commits.map(
      (commit): TimelineItem => ({ kind: "commit", at: commit.committedDate, commit }),
    ),
  ];
  return items.toSorted((a, b) => a.at.localeCompare(b.at));
}

export function PullRequestTimelineTab({
  reference,
  activity,
  isLoading,
  error,
  onRetry,
}: {
  reference: PullRequestRef;
  activity: PullRequestActivity | undefined;
  isLoading: boolean;
  error: unknown;
  onRetry: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");

  const comment = useMutation(pullRequestsCommentMutationOptions({ queryClient }));

  const trimmedDraft = draft.trim();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </div>
        ) : error ? (
          <PullRequestFailure
            error={error}
            fallbackTitle="Could not load the conversation"
            onRetry={onRetry}
          />
        ) : activity ? (
          <TimelineBody activity={activity} />
        ) : null}
      </div>

      {/* ── Add to it ─────────────────────────────────────────────────── */}
      <div className="shrink-0 space-y-2 border-t border-border/60 p-3">
        <Textarea
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Leave a comment…"
          rows={3}
          disabled={comment.isPending}
          aria-label="Comment on this pull request"
        />
        <div className="flex justify-end">
          <Button
            size="xs"
            disabled={comment.isPending || trimmedDraft.length === 0}
            onClick={() =>
              comment.mutate(
                { ...reference, body: draft },
                {
                  // The builder's own `onSuccess` invalidates the timeline; clearing the box is
                  // this component's half of the same event.
                  onSuccess: () => setDraft(""),
                  onError: (mutationError) => {
                    toastManager.add({
                      type: "error",
                      title: "Could not post the comment",
                      description: readPullRequestErrorMessage(
                        mutationError,
                        "GitHub refused the request.",
                      ),
                    });
                  },
                },
              )
            }
          >
            {comment.isPending ? "Posting…" : "Comment"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function TimelineBody({ activity }: { activity: PullRequestActivity }) {
  const items = buildTimeline(activity);

  if (items.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground/70">
        Nothing has happened on this pull request yet.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {activity.truncated ? (
        <p className="rounded-lg border border-border/60 bg-background/40 px-2.5 py-1.5 text-[11px] text-muted-foreground">
          Only the most recent part of this conversation was read. Open it on GitHub for the rest.
        </p>
      ) : null}

      {items.map((item) =>
        item.kind === "comment" ? (
          <CommentCard key={`${item.comment.kind}:${item.comment.id}`} comment={item.comment} />
        ) : (
          <CommitRow key={item.commit.oid} commit={item.commit} />
        ),
      )}
    </div>
  );
}

function CommentCard({ comment }: { comment: PullRequestComment }) {
  const reviewLabel = comment.reviewState ? REVIEW_STATE_LABELS[comment.reviewState] : null;

  return (
    <article className="rounded-xl border border-border/60 bg-background/40 px-3 py-2.5">
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-muted-foreground">
        <MessageIcon className="size-3 shrink-0 opacity-60" aria-hidden />
        <PullRequestAuthorChip author={comment.author} />
        {reviewLabel ? (
          <span
            className={cn(
              "font-medium",
              REVIEW_STATE_CLASS[comment.reviewState ?? ""] ?? "text-muted-foreground",
            )}
          >
            {reviewLabel}
          </span>
        ) : null}
        {comment.kind === "inline" && comment.path ? (
          <span className="truncate font-mono text-muted-foreground/70">
            {comment.path}
            {comment.line === null ? "" : `:${comment.line}`}
          </span>
        ) : null}
        <span className="ms-auto shrink-0 tabular-nums">
          {formatRelativeTime(comment.createdAt)}
        </span>
      </header>
      {comment.body.trim().length > 0 ? (
        <div className="mt-1.5">
          <ChatMarkdown text={comment.body} cwd={undefined} isStreaming={false} />
        </div>
      ) : null}
    </article>
  );
}

function CommitRow({ commit }: { commit: PullRequestCommit }) {
  return (
    <div className="flex items-center gap-2 px-1 text-[11px] text-muted-foreground">
      <GitCommitIcon className="size-3 shrink-0 opacity-60" />
      <span className="shrink-0 font-mono text-muted-foreground/70">{commit.oid.slice(0, 7)}</span>
      <span className="min-w-0 flex-1 truncate">{commit.messageHeadline}</span>
      <PullRequestAuthorChip author={commit.author} className="shrink-0" />
      <span className="shrink-0 tabular-nums">{formatRelativeTime(commit.committedDate)}</span>
    </div>
  );
}
